import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import * as PIXI from 'pixi.js';
import { Live2DModel } from 'pixi-live2d-display';
import { Face, Vector, Utils } from 'kalidokit';
import { useParticipant } from '@videosdk.live/react-sdk';

const { lerp } = Vector;
const { clamp } = Utils;

const Live2DViewer = ({ 
  selectedAvatar = null, 
  isRemote = false, 
  participantId,
  remoteParticipantId = null,
  localAvatarPath = null,
  remoteAvatarPath = null
}) => {
  const [isRemoteModelLoading, setIsRemoteModelLoading] = useState(false);
  const [isMediaPipeInitializing, setIsMediaPipeInitializing] = useState(false);
  const [isRemoteMediaPipeInitializing, setIsRemoteMediaPipeInitializing] = useState(false);
  const [debugInfo, setDebugInfo] = useState({ local: null, remote: null });
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [localFaceDetected, setLocalFaceDetected] = useState(false);
  const [remoteFaceDetected, setRemoteFaceDetected] = useState(false);
  const [lastLocalFaceTime, setLastLocalFaceTime] = useState(0);
  const [lastRemoteFaceTime, setLastRemoteFaceTime] = useState(0);
    const canvasRef = useRef(null);
    const videoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const combinedCanvasRef = useRef(null);
    const faceData = useRef(null);
    const remoteFaceData = useRef(null);
    const faceMeshInstanceRef = useRef(null);
    const cameraInstanceRef = useRef(null);
    const remoteFaceMeshInstanceRef = useRef(null);
    const remoteCameraInstanceRef = useRef(null);
    
    // PIXI app and model refs
    const pixiAppRef = useRef(null);
    const localModelRef = useRef(null);
    const remoteModelRef = useRef(null);
    const isInitializedRef = useRef(false);
    
    // Always call useParticipant hook (Rules of Hooks requirement)
    const participantData = useParticipant(participantId || "");
    const remoteParticipantData = useParticipant(remoteParticipantId || "");
    
    // Helper function to extract avatar ID from participant metadata
    const getParticipantAvatarId = useCallback((participant) => {
        if (participant?.metaData) {
            try {
                const metadata = typeof participant.metaData === 'string' 
                    ? JSON.parse(participant.metaData) 
                    : participant.metaData;
                return metadata.avatarId || 'm1';
            } catch (error) {
                console.warn('[Live2D] Error parsing participant metadata:', error);
            }
        }
        return 'm1'; // Default fallback
    }, []);
    
    // Get avatar IDs from participant metadata (for fallback only)
    const localAvatarId = useMemo(() => 
        getParticipantAvatarId(participantData), 
        [participantData, getParticipantAvatarId]
    );
    
    const remoteAvatarId = useMemo(() => 
        getParticipantAvatarId(remoteParticipantData), 
        [remoteParticipantData, getParticipantAvatarId]
    );
    
    // Create video stream for local user (right model)
    const videoStream = useMemo(() => {
        if (participantId && participantData && participantData.webcamOn && participantData.webcamStream) {
            const mediaStream = new MediaStream();
            mediaStream.addTrack(participantData.webcamStream.track);
            // console.log(`[Live2D] LOCAL using participant ${participantId}`);
            return mediaStream;
        }
        // console.log(`[Live2D] LOCAL has no stream - participantId: ${participantId}`);
        return null;
    }, [participantId, participantData?.webcamStream, participantData?.webcamOn]);
    
    // Create video stream for remote user (left model)
    const remoteVideoStream = useMemo(() => {
        if (remoteParticipantId && remoteParticipantData && remoteParticipantData.webcamOn && remoteParticipantData.webcamStream) {
            const mediaStream = new MediaStream();
            mediaStream.addTrack(remoteParticipantData.webcamStream.track);
            // console.log(`[Live2D] REMOTE using participant ${remoteParticipantId}`);
            return mediaStream;
        }
        // console.log(`[Live2D] REMOTE has no stream - remoteParticipantId: ${remoteParticipantId}`);
        return null;
    }, [remoteParticipantId, remoteParticipantData?.webcamStream, remoteParticipantData?.webcamOn]);
    
    // Camera stream assignment
    const cameraStream = useMemo(() => {
        return videoStream || null;
    }, [videoStream]);
    
    const remoteCameraStream = useMemo(() => {
        return remoteVideoStream || null;
    }, [remoteVideoStream]);

    // Check if device is mobile
    const isMobile = useMemo(() => {
        return window.innerWidth <= 768; // Mobile breakpoint
    }, []);

    // Dynamic scale calculation based on model dimensions and available space
    const calculateModelScale = useCallback((model, isRemote = false) => {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        // Define available area
        let availableWidth, availableHeight;
        
        if (isMobile) {
            // Mobile: each model gets half the screen height
            availableWidth = screenWidth;
            availableHeight = screenHeight / 2;
        } else {
            // Desktop: each model gets half the screen width
            availableWidth = screenWidth / 2;
            availableHeight = screenHeight;
        }
        
        // Calculate scale factors for both dimensions
        const scaleX = availableWidth / model.width;
        const scaleY = availableHeight / model.height;
        
        // Use the smaller scale to ensure model fits completely in available space
        let scale = Math.min(scaleX, scaleY);
        
        // Apply a small padding factor (90%) to ensure some margin
        scale = scale * 0.9;
        
        // Clamp scale between reasonable bounds
        scale = Math.max(0.2, Math.min(scale, 2.0));
        
        // Store debug info
        const debugData = {
            originalDimensions: `${model.width} x ${model.height}`,
            availableSpace: `${availableWidth} x ${availableHeight}`,
            targetSize: `${(availableWidth * 0.9).toFixed(0)} x ${(availableHeight * 0.9).toFixed(0)}`,
            scaleCalculations: `X=${scaleX.toFixed(3)}, Y=${scaleY.toFixed(3)}`,
            finalScale: scale.toFixed(3),
            finalSize: `${(model.width * scale).toFixed(0)} x ${(model.height * scale).toFixed(0)}`
        };
        
        setDebugInfo(prev => ({
            ...prev,
            [isRemote ? 'remote' : 'local']: debugData
        }));
        
        return scale;
    }, [isMobile]);

    // Simple and reliable positioning
    const positionLocalModel = useCallback((model) => {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        // Set scale first
        const scale = calculateModelScale(model, false);
        model.scale.set(scale);
        
        // Set anchor to top center for proper head positioning
        model.anchor.set(0.5, 0);
        
        let posX, posY;
        
        if (isMobile) {
            // Mobile: bottom half - position from top
            posX = screenWidth / 2;
            posY = 0; // No padding from top
        } else {
            // Desktop: right half - position from top
            posX = screenWidth * 0.75;
            posY = 0; // No padding from top
        }
        
        model.position.set(posX, posY);
        
        // Update debug info with position data
        setDebugInfo(prev => ({
            ...prev,
            local: {
                ...prev.local,
                screenDimensions: `${screenWidth} x ${screenHeight}`,
                position: `(${posX.toFixed(0)}, ${posY.toFixed(0)})`,
                scale: scale.toFixed(3),
                modelSize: `${(model.width * scale).toFixed(0)} x ${(model.height * scale).toFixed(0)}`
            }
        }));
    }, [isMobile, calculateModelScale]);

    // Simple and reliable positioning for remote model
    const positionRemoteModel = useCallback((model) => {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        // Set scale first
        const scale = calculateModelScale(model, true);
        model.scale.set(scale);
        
        // Set anchor to top center for proper head positioning
        model.anchor.set(0.5, 0);
        
        let posX, posY;
        
        if (isMobile) {
            // Mobile: top half - position from top
            posX = screenWidth / 2;
            posY = 0; // No padding from top
        } else {
            // Desktop: left half - position from top
            posX = screenWidth * 0.25;
            posY = 0; // No padding from top
        }
        
        model.position.set(posX, posY);
        
        // Update debug info with position data
        setDebugInfo(prev => ({
            ...prev,
            remote: {
                ...prev.remote,
                screenDimensions: `${screenWidth} x ${screenHeight}`,
                position: `(${posX.toFixed(0)}, ${posY.toFixed(0)})`,
                scale: scale.toFixed(3),
                modelSize: `${(model.width * scale).toFixed(0)} x ${(model.height * scale).toFixed(0)}`
            }
        }));
    }, [isMobile, calculateModelScale]);

    // Rig face function for local model
    const rigFace = useCallback((result, lerpAmount = 0.7) => {
        const model = localModelRef.current;
        if (!model || !result) return;
        const coreModel = model.internalModel.coreModel;

        model.internalModel.eyeBlink = undefined;

        coreModel.setParameterValueById("ParamEyeBallX", lerp(result.pupil.x, coreModel.getParameterValueById("ParamEyeBallX"), lerpAmount));
        coreModel.setParameterValueById("ParamEyeBallY", lerp(result.pupil.y, coreModel.getParameterValueById("ParamEyeBallY"), lerpAmount));

        coreModel.setParameterValueById("ParamAngleX", lerp(result.head.degrees.y, coreModel.getParameterValueById("ParamAngleX"), lerpAmount));
        coreModel.setParameterValueById("ParamAngleY", lerp(result.head.degrees.x, coreModel.getParameterValueById("ParamAngleY"), lerpAmount));
        coreModel.setParameterValueById("ParamAngleZ", lerp(result.head.degrees.z, coreModel.getParameterValueById("ParamAngleZ"), lerpAmount));

        const dampener = 0.2;
        coreModel.setParameterValueById("ParamBodyAngleX", lerp(result.head.degrees.y * dampener, coreModel.getParameterValueById("ParamBodyAngleX"), lerpAmount));
        coreModel.setParameterValueById("ParamBodyAngleY", lerp(result.head.degrees.x * dampener, coreModel.getParameterValueById("ParamBodyAngleY"), lerpAmount));
        coreModel.setParameterValueById("ParamBodyAngleZ", lerp(result.head.degrees.z * dampener, coreModel.getParameterValueById("ParamBodyAngleZ"), lerpAmount));

        let stabilizedEyes = Face.stabilizeBlink(
            {
                l: lerp(result.eye.l, coreModel.getParameterValueById("ParamEyeLOpen"), 0.7),
                r: lerp(result.eye.r, coreModel.getParameterValueById("ParamEyeROpen"), 0.7),
            },
            result.head.y
        );

        coreModel.setParameterValueById("ParamEyeLOpen", stabilizedEyes.l);
        coreModel.setParameterValueById("ParamEyeROpen", stabilizedEyes.r);

        coreModel.setParameterValueById("ParamMouthOpenY", lerp(result.mouth.y, coreModel.getParameterValueById("ParamMouthOpenY"), 0.3));
        coreModel.setParameterValueById("ParamMouthForm", 0.3 + lerp(result.mouth.x, coreModel.getParameterValueById("ParamMouthForm"), 0.3));

        coreModel.update();
    }, []);

    // Rig face function for remote model
    const rigFaceRemote = useCallback((result, lerpAmount = 0.7) => {
        const model = remoteModelRef.current;
                if (!model || !result) return;
                const coreModel = model.internalModel.coreModel;

                model.internalModel.eyeBlink = undefined;

                coreModel.setParameterValueById("ParamEyeBallX", lerp(result.pupil.x, coreModel.getParameterValueById("ParamEyeBallX"), lerpAmount));
                coreModel.setParameterValueById("ParamEyeBallY", lerp(result.pupil.y, coreModel.getParameterValueById("ParamEyeBallY"), lerpAmount));

                coreModel.setParameterValueById("ParamAngleX", lerp(result.head.degrees.y, coreModel.getParameterValueById("ParamAngleX"), lerpAmount));
                coreModel.setParameterValueById("ParamAngleY", lerp(result.head.degrees.x, coreModel.getParameterValueById("ParamAngleY"), lerpAmount));
                coreModel.setParameterValueById("ParamAngleZ", lerp(result.head.degrees.z, coreModel.getParameterValueById("ParamAngleZ"), lerpAmount));

                const dampener = 0.2;
                coreModel.setParameterValueById("ParamBodyAngleX", lerp(result.head.degrees.y * dampener, coreModel.getParameterValueById("ParamBodyAngleX"), lerpAmount));
                coreModel.setParameterValueById("ParamBodyAngleY", lerp(result.head.degrees.x * dampener, coreModel.getParameterValueById("ParamBodyAngleY"), lerpAmount));
                coreModel.setParameterValueById("ParamBodyAngleZ", lerp(result.head.degrees.z * dampener, coreModel.getParameterValueById("ParamBodyAngleZ"), lerpAmount));

                let stabilizedEyes = Face.stabilizeBlink(
                    {
                        l: lerp(result.eye.l, coreModel.getParameterValueById("ParamEyeLOpen"), 0.7),
                        r: lerp(result.eye.r, coreModel.getParameterValueById("ParamEyeROpen"), 0.7),
                    },
                    result.head.y
                );

                coreModel.setParameterValueById("ParamEyeLOpen", stabilizedEyes.l);
                coreModel.setParameterValueById("ParamEyeROpen", stabilizedEyes.r);

                coreModel.setParameterValueById("ParamMouthOpenY", lerp(result.mouth.y, coreModel.getParameterValueById("ParamMouthOpenY"), 0.3));
                coreModel.setParameterValueById("ParamMouthForm", 0.3 + lerp(result.mouth.x, coreModel.getParameterValueById("ParamMouthForm"), 0.3));

                coreModel.update();
    }, []);

    // Window resize handler for responsive positioning
    const handleResize = useCallback(() => {
        if (localModelRef.current) {
            positionLocalModel(localModelRef.current);
        }
        if (remoteModelRef.current) {
            positionRemoteModel(remoteModelRef.current);
        }
    }, [positionLocalModel, positionRemoteModel]);

    // Initialize PIXI app once
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://cubism.live2d.com/sdk-web/cubismcore/live2dcubismcore.min.js';
        script.async = true;
        document.body.appendChild(script);

        script.onload = async () => {
            if (isInitializedRef.current || !canvasRef.current) return;
            
            // console.log('[Live2D] Initializing PIXI app');
            // Wait a bit to ensure canvas is ready
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const app = new PIXI.Application({
                view: canvasRef.current,
                autoStart: true,
                backgroundAlpha: 0, 
                transparent: true,
                resizeTo: window,
            });

            app.stage.alpha = 1;
            pixiAppRef.current = app;
            isInitializedRef.current = true;

            // Setup animation ticker
            app.ticker.add(() => {
                if (faceData.current && localModelRef.current) {
                    rigFace(faceData.current, 0.5);
                }
                if (remoteFaceData.current && remoteModelRef.current) {
                    rigFaceRemote(remoteFaceData.current, 0.5);
                    // console.log('[Live2D] Remote model animated');
                } else if (remoteModelRef.current) {
                    // console.log('[Live2D] Remote model not animating - no face data or model');
                }
            });

            // Add window resize handler for responsive positioning
            window.addEventListener('resize', handleResize);

            // Load local model immediately
            loadLocalModel();

            // console.log('[Live2D] PIXI app initialized');
        };

        return () => {
            // Remove resize event listener
            window.removeEventListener('resize', handleResize);
            
            if (pixiAppRef.current) {
                pixiAppRef.current.destroy(true, true);
                pixiAppRef.current = null;
            }
            isInitializedRef.current = false;
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [handleResize]); // Include handleResize in dependencies

    // Reload local model when avatar path or participant metadata changes
    useEffect(() => {
        if (pixiAppRef.current && (localAvatarPath || localAvatarId)) {
            // Always reload when avatar changes (either path or metadata)
            loadLocalModel();
        }
    }, [localAvatarPath, localAvatarId]);

    // Load local model
    const loadLocalModel = async () => {
        if (!pixiAppRef.current) return;
        
        // Always remove existing local model before loading new one
        if (localModelRef.current) {
            try {
                pixiAppRef.current.stage.removeChild(localModelRef.current);
                localModelRef.current.destroy();
                localModelRef.current = null;
            } catch (error) {
                console.warn('[Live2D] Error during local model cleanup:', error);
                localModelRef.current = null;
            }
        }
        
        // Get model path from participant metadata or use provided path
        let modelPath = localAvatarPath;
        if (!modelPath) {
            // Map avatar ID to Live2D model path
            const avatarIdToModelPath = {
                'm1': '/live2d/models/hiyori/hiyori_pro_t10.model3.json',
                'm2': '/live2d/models/huohuo/huohuo.model3.json',
                'm3': '/live2d/models/fuxuan/fuxuan.model3.json',
                'm4': '/live2d/models/miku/runtime/miku.model3.json',
                'm5': '/live2d/models/hiyori/hiyori_pro_t10.model3.json'
            };
            modelPath = avatarIdToModelPath[localAvatarId] || '/live2d/models/hiyori/hiyori_pro_t10.model3.json';
        }
        
        console.log('[Live2D] Loading local model from:', modelPath, 'for participant:', participantId);
        try {
            const model = await Live2DModel.from(modelPath, { autoInteract: false });
            
            // Position local model (right half on desktop, bottom half on mobile)
            positionLocalModel(model);

            pixiAppRef.current.stage.addChild(model);
            localModelRef.current = model;
            // console.log('[Live2D] Local model loaded');
        } catch (error) {
            console.error('[Live2D] Failed to load local model:', error);
        }
    };

    // Load remote model when remote participant joins
    useEffect(() => {
        // Helper function to validate model file exists
        const validateModelFile = async (modelPath) => {
            try {
                const response = await fetch(modelPath, { method: 'HEAD' });
                return response.ok;
            } catch (error) {
                console.warn(`[Live2D] Model validation failed for ${modelPath}:`, error);
                return false;
            }
        };

        const loadRemoteModel = async (retryCount = 0, useFallback = false) => {
            if (!pixiAppRef.current || !remoteParticipantId) return;
            
            // Don't load remote model if we don't have a valid avatar path
            if (!remoteAvatarPath && !remoteAvatarId) {
                console.log('[Live2D] Skipping remote model load - no avatar path or ID available');
                return;
            }
            
            // If there's already a remote model, remove it first to reload with new avatar
            if (remoteModelRef.current) {
                // console.log('[Live2D] Removing existing remote model to reload with new avatar');
                try {
                    pixiAppRef.current.stage.removeChild(remoteModelRef.current);
                    remoteModelRef.current.destroy();
                    remoteModelRef.current = null;
                } catch (error) {
                    console.warn('[Live2D] Error during remote model cleanup:', error);
                    remoteModelRef.current = null;
                }
            }
            
            setIsRemoteModelLoading(true);
            
            // Get model path from remote participant metadata or use provided path
            let modelPath = remoteAvatarPath;
            if (!modelPath) {
                // Map avatar ID to Live2D model path (fallback only)
                const avatarIdToModelPath = {
                    'm1': '/live2d/models/hiyori/hiyori_pro_t10.model3.json',
                    'm2': '/live2d/models/huohuo/huohuo.model3.json',
                    'm3': '/live2d/models/fuxuan/fuxuan.model3.json',
                    'm4': '/live2d/models/miku/runtime/miku.model3.json',
                    'm5': '/live2d/models/hiyori/hiyori_pro_t10.model3.json'
                };
                modelPath = avatarIdToModelPath[remoteAvatarId] || '/live2d/models/hiyori/hiyori_pro_t10.model3.json';
            }
            
            console.log('[Live2D] Remote model loading debug:');
            console.log('[Live2D] - remoteAvatarPath prop:', remoteAvatarPath);
            console.log('[Live2D] - remoteParticipantId:', remoteParticipantId);
            console.log('[Live2D] - selected modelPath:', modelPath);
            console.log('[Live2D] - retryCount:', retryCount);
            console.log('[Live2D] - useFallback:', useFallback);
            
            // If using fallback, try alternative models
            if (useFallback) {
                const fallbackModels = [
                    '/live2d/models/hiyori/hiyori_pro_t10.model3.json',
                    '/live2d/models/huohuo/huohuo.model3.json',
                    '/live2d/models/fuxuan/fuxuan.model3.json',
                    '/live2d/models/miku/runtime/miku.model3.json'
                ];
                
                // Skip the current model path if it's in the fallback list
                const currentIndex = fallbackModels.indexOf(modelPath);
                if (currentIndex !== -1) {
                    fallbackModels.splice(currentIndex, 1);
                }
                
                // Try each fallback model in sequence
                for (let i = 0; i < fallbackModels.length; i++) {
                    const fallbackPath = fallbackModels[i];
                    // console.log(`[Live2D] Trying fallback model ${i + 1}/${fallbackModels.length}: ${fallbackPath}`);
                    
                    try {
                        const isValid = await validateModelFile(fallbackPath);
                        if (isValid) {
                            modelPath = fallbackPath;
                            // console.log(`[Live2D] Selected fallback model: ${modelPath}`);
                            break;
                        } else {
                            console.warn(`[Live2D] Fallback model not available: ${fallbackPath}`);
                        }
                    } catch (error) {
                        console.warn(`[Live2D] Error validating fallback model ${fallbackPath}:`, error);
                    }
                }
                
                // If no valid fallback found, use the first one as last resort
                if (modelPath === (remoteAvatarPath || '/live2d/models/hiyori/hiyori_pro_t10.model3.json')) {
                    modelPath = fallbackModels[0] || '/live2d/models/hiyori/hiyori_pro_t10.model3.json';
                    // console.log(`[Live2D] Using last resort fallback model: ${modelPath}`);
                }
            }
            
            console.log(`[Live2D] Loading remote model from: ${modelPath} (attempt ${retryCount + 1}) for remote participant: ${remoteParticipantId}`);
            
            try {
                // Validate model file exists before loading
                const isValid = await validateModelFile(modelPath);
                if (!isValid) {
                    throw new Error(`Model file not found or invalid: ${modelPath}`);
                }
                
                const model = await Live2DModel.from(modelPath, { autoInteract: false });
                
                // Position remote model (left half on desktop, top half on mobile)
                positionRemoteModel(model);

                pixiAppRef.current.stage.addChild(model);
                remoteModelRef.current = model;
                // console.log('[Live2D] Remote model loaded successfully');
                setIsRemoteModelLoading(false);
            } catch (error) {
                console.error(`[Live2D] Failed to load remote model (attempt ${retryCount + 1}):`, error);
                
                // Retry logic with exponential backoff
                if (retryCount < 3) {
                    const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
                    // console.log(`[Live2D] Retrying remote model load in ${delay}ms...`);
                    
                    setTimeout(() => {
                        // Check if conditions are still valid before retrying
                        if (pixiAppRef.current && remoteParticipantId && !remoteModelRef.current) {
                            loadRemoteModel(retryCount + 1, useFallback);
                        }
                    }, delay);
                } else if (!useFallback) {
                    // If all retries failed with primary model, try fallback
                    // console.log('[Live2D] All retries failed, trying fallback models...');
                    setTimeout(() => {
                        if (pixiAppRef.current && remoteParticipantId && !remoteModelRef.current) {
                            loadRemoteModel(0, true);
                        }
                    }, 1000);
                } else {
                    console.error('[Live2D] Failed to load remote model after all retry attempts and fallbacks');
                    setIsRemoteModelLoading(false);
                }
            }
        };

        // Add a small delay to ensure PIXI app is fully ready
        const loadWithDelay = () => {
            if (remoteParticipantId && pixiAppRef.current) {
                // Small delay to ensure PIXI app is fully initialized
                setTimeout(() => {
                    if (pixiAppRef.current && remoteParticipantId) {
                        loadRemoteModel();
                    }
                }, 500);
            } else if (remoteParticipantId && !pixiAppRef.current) {
                // If PIXI app is not ready yet, wait and retry
                // console.log('[Live2D] PIXI app not ready for remote model, waiting...');
                setTimeout(() => {
                    if (remoteParticipantId) {
                        loadWithDelay();
                    }
                }, 1000);
            }
        };

        loadWithDelay();
    }, [remoteParticipantId, remoteAvatarPath]);

    // Remove remote model when remote participant leaves
    useEffect(() => {
        // console.log('[Live2D] Remote participant cleanup effect triggered:', {
        //     remoteParticipantId,
        //     hasRemoteModel: !!remoteModelRef.current,
        //     hasPixiApp: !!pixiAppRef.current
        // });
        
        if (!remoteParticipantId && remoteModelRef.current && pixiAppRef.current) {
            // console.log('[Live2D] Removing remote model - participant left');
            try {
                pixiAppRef.current.stage.removeChild(remoteModelRef.current);
                remoteModelRef.current.destroy();
                remoteModelRef.current = null;
                // console.log('[Live2D] Remote model removed successfully');
            } catch (error) {
                console.warn('[Live2D] Error during remote model cleanup:', error);
                remoteModelRef.current = null;
            }
        } else if (!remoteParticipantId) {
            // console.log('[Live2D] No remote participant, but conditions not met for cleanup:', {
            //     hasRemoteModel: !!remoteModelRef.current,
            //     hasPixiApp: !!pixiAppRef.current
            // });
        }
        
        // Reset loading state when remote participant leaves
        if (!remoteParticipantId) {
            setIsRemoteModelLoading(false);
            
            // Force cleanup of remote model even if PIXI app is not available
            if (remoteModelRef.current) {
                // console.log('[Live2D] Force cleaning up remote model - participant left');
                try {
                    if (pixiAppRef.current && pixiAppRef.current.stage) {
                        pixiAppRef.current.stage.removeChild(remoteModelRef.current);
                    }
                    remoteModelRef.current.destroy();
                    remoteModelRef.current = null;
                    // console.log('[Live2D] Remote model force cleaned up successfully');
                } catch (error) {
                    console.warn('[Live2D] Error during force cleanup:', error);
                    remoteModelRef.current = null;
                }
            }
        }
    }, [remoteParticipantId]);

    // Set up local video stream for MediaPipe processing
    useEffect(() => {
        if (cameraStream && videoRef.current) {
            // console.log(`[Live2D] Setting up LOCAL stream`);
            videoRef.current.srcObject = cameraStream;
            
            videoRef.current.onloadedmetadata = () => {
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            // console.log(`[Live2D] Local video stream playing`);
                            // Setup MediaPipe after video is playing
                            setupMediaPipe();
                        })
                        .catch((error) => {
                            if (error.name !== 'AbortError') {
                                console.error(`[Live2D] Local video play failed:`, error);
                            }
                        });
                }
            };
        } else if (!cameraStream && videoRef.current) {
            videoRef.current.srcObject = null;
            setIsMediaPipeInitializing(false);
            if (remoteParticipantId) {
                setIsRemoteMediaPipeInitializing(false);
            }
            // Cleanup MediaPipe when stream is removed
            if (cameraInstanceRef.current) {
                cameraInstanceRef.current.stop();
                cameraInstanceRef.current = null;
            }
            if (faceMeshInstanceRef.current) {
                faceMeshInstanceRef.current.close();
                faceMeshInstanceRef.current = null;
            }
        }
    }, [cameraStream]);

    // Setup MediaPipe for both local and remote streams
    const setupMediaPipe = async () => {
        if (!window.FaceMesh) return;
        
        // Cleanup existing instances
        if (cameraInstanceRef.current) {
            cameraInstanceRef.current.stop();
            cameraInstanceRef.current = null;
        }
        if (faceMeshInstanceRef.current) {
            faceMeshInstanceRef.current.close();
            faceMeshInstanceRef.current = null;
        }

        // console.log('[Live2D] Setting up MediaPipe for both streams');
        setIsMediaPipeInitializing(true);
        // Only set remote MediaPipe state if there's a remote participant
        if (remoteParticipantId) {
            setIsRemoteMediaPipeInitializing(true);
        }
        
        try {
            // Add delay to prevent WASM module conflicts
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            faceMeshInstanceRef.current = new window.FaceMesh({
                locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
            });

            faceMeshInstanceRef.current.setOptions({
                maxNumFaces: 2, // Allow 2 faces for local and remote
                refineLandmarks: true,
                minDetectionConfidence: 0.5,
                minTrackingConfidence: 0.5,
            });

            faceMeshInstanceRef.current.onResults((results) => {
                // console.log('[Live2D] MediaPipe results:', {
                //     multiFaceLandmarks: results.multiFaceLandmarks?.length || 0,
                //     localVideoReady: videoRef.current?.videoWidth > 0,
                //     remoteVideoReady: remoteVideoRef.current?.videoWidth > 0
                // });
                
                let localFaceFound = false;
                let remoteFaceFound = false;
                
                if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
                    // Process all detected faces
                    results.multiFaceLandmarks.forEach((landmarks, index) => {
                        // Determine which video stream this face belongs to based on position
                        const faceCenterX = landmarks.reduce((sum, point) => sum + point.x, 0) / landmarks.length;
                        
                        if (faceCenterX < 0.5) {
                            // Face is in left half (local video)
                            faceData.current = Face.solve(landmarks, {
                                runtime: 'mediapipe',
                                video: videoRef.current,
                            });
                            localFaceFound = true;
                            // console.log('[Live2D] Local face data updated');
                        } else {
                            // Face is in right half (remote video)
                            remoteFaceData.current = Face.solve(landmarks, {
                                runtime: 'mediapipe',
                                video: remoteVideoRef.current,
                            });
                            remoteFaceFound = true;
                            // console.log('[Live2D] Remote face data updated');
                        }
                    });
                }
                
                // Update face detection status with timestamps
                if (localFaceFound) {
                    setLocalFaceDetected(true);
                    setLastLocalFaceTime(Date.now());
                }
                
                if (remoteFaceFound) {
                    setRemoteFaceDetected(true);
                    setLastRemoteFaceTime(Date.now());
                }
            });

            // Add another small delay before starting camera
            await new Promise(resolve => setTimeout(resolve, 200));

            // Create a combined canvas to merge both video streams
            const combinedCanvas = document.createElement('canvas');
            const combinedCtx = combinedCanvas.getContext('2d');
            combinedCanvas.width = 640; // Double width to fit both videos side by side
            combinedCanvas.height = 180;
            
            cameraInstanceRef.current = new window.Camera(videoRef.current, {
                onFrame: async () => {
                    if (faceMeshInstanceRef.current && videoRef.current && remoteVideoRef.current) {
                        // Draw local video on left half
                        combinedCtx.drawImage(videoRef.current, 0, 0, 320, 180);
                        // Draw remote video on right half
                        combinedCtx.drawImage(remoteVideoRef.current, 320, 0, 320, 180);
                        
                        // Send combined canvas to MediaPipe
                        await faceMeshInstanceRef.current.send({ image: combinedCanvas });
                    } else if (faceMeshInstanceRef.current && videoRef.current) {
                        // Fallback to local video only
                        await faceMeshInstanceRef.current.send({ image: videoRef.current });
                    }
                },
                width: 320,
                height: 180,
            });
            
            cameraInstanceRef.current.start();
            // console.log('[Live2D] MediaPipe setup completed for both streams');
            setIsMediaPipeInitializing(false);
            if (remoteParticipantId) {
                setIsRemoteMediaPipeInitializing(false);
            }
            
            // Initialize face detection status
            setLocalFaceDetected(false);
            setRemoteFaceDetected(false);
            setLastLocalFaceTime(0);
            setLastRemoteFaceTime(0);
        } catch (error) {
            console.error('[Live2D] MediaPipe setup failed:', error);
            setIsMediaPipeInitializing(false);
            if (remoteParticipantId) {
                setIsRemoteMediaPipeInitializing(false);
            }
        }
    };

    // Set up remote video stream
    useEffect(() => {
        if (remoteCameraStream && remoteVideoRef.current) {
            // console.log(`[Live2D] Setting up REMOTE stream`);
            remoteVideoRef.current.srcObject = remoteCameraStream;
            
            remoteVideoRef.current.onloadedmetadata = () => {
                const playPromise = remoteVideoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            // console.log(`[Live2D] Remote video stream playing`);
                        })
                        .catch((error) => {
                            if (error.name !== 'AbortError') {
                                console.error(`[Live2D] Remote video play failed:`, error);
                            }
                        });
                }
            };
        } else if (!remoteCameraStream && remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
        }
    }, [remoteCameraStream]);



    // Cleanup effect
    useEffect(() => {
        return () => {
            // cleanup MediaPipe instances
            if (cameraInstanceRef.current) {
                cameraInstanceRef.current.stop();
                cameraInstanceRef.current = null;
            }
            if (faceMeshInstanceRef.current) {
                faceMeshInstanceRef.current.close();
                faceMeshInstanceRef.current = null;
            }
            if (remoteCameraInstanceRef.current) {
                remoteCameraInstanceRef.current.stop();
                remoteCameraInstanceRef.current = null;
            }
            if (remoteFaceMeshInstanceRef.current) {
                remoteFaceMeshInstanceRef.current.close();
                remoteFaceMeshInstanceRef.current = null;
            }
        };
    }, []);

    // Continuous face detection monitoring
    useEffect(() => {
        const checkFaceDetection = () => {
            const now = Date.now();
            const faceTimeout = 3000; // 3 seconds without face detection = face lost
            
            // Check local face detection
            if (localFaceDetected && (now - lastLocalFaceTime) > faceTimeout) {
                setLocalFaceDetected(false);
            }
            
            // Check remote face detection
            if (remoteFaceDetected && (now - lastRemoteFaceTime) > faceTimeout) {
                setRemoteFaceDetected(false);
            }
        };
        
        const interval = setInterval(checkFaceDetection, 1000); // Check every second
        
        return () => clearInterval(interval);
    }, [localFaceDetected, remoteFaceDetected, lastLocalFaceTime, lastRemoteFaceTime]);

    return (
        <div>
            <video  ref={videoRef} hidden playsInline muted autoPlay></video>
            <video ref={remoteVideoRef} hidden playsInline muted autoPlay></video>
            <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100vw', height: '100vh' }}></canvas>
            
            {/* Small Remote Model Loading Spinner */}
            {isRemoteModelLoading && (
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    padding: '8px 12px',
                    borderRadius: '20px',
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #00ff88',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500'
                    }}>
                        Loading remote model...
                    </span>
                </div>
            )}

            {/* Local MediaPipe Initialization Spinner (Right side - Local user area) */}
            {isMediaPipeInitializing && (
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    padding: '8px 12px',
                    borderRadius: '20px',
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #ff8800',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500'
                    }}>
                        Initializing Camera...
                    </span>
                </div>
            )}

            {/* Remote MediaPipe Initialization Spinner (Left side - Remote user area) */}
            {isRemoteMediaPipeInitializing && remoteParticipantId && (
                <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    padding: '8px 12px',
                    borderRadius: '20px',
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #ff0088',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500'
                    }}>
                        Initializing Remote Camera...
                    </span>
                </div>
            )}

            {/* Local Face Lost Message (Right side - Local user area) */}
            {!localFaceDetected && !isMediaPipeInitializing && (
                <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    right: '120px',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(255, 0, 0, 0.8)',
                    padding: '8px 12px',
                    borderRadius: '20px',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{
                        width: '12px',
                        height: '12px',
                        background: '#ff4444',
                        borderRadius: '50%',
                        animation: 'pulse 2s infinite'
                    }} />
                    <span style={{
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500'
                    }}>
                        Face not detected, come closer
                    </span>
                </div>
            )}

            {/* Remote Face Lost Message (Left side - Remote user area) */}
            {!remoteFaceDetected && remoteParticipantId && !isRemoteMediaPipeInitializing && (
                <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '120px',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(255, 0, 0, 0.8)',
                    padding: '8px 12px',
                    borderRadius: '20px',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{
                        width: '12px',
                        height: '12px',
                        background: '#ff4444',
                        borderRadius: '50%',
                        animation: 'pulse 2s infinite'
                    }} />
                    <span style={{
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '500'
                    }}>
                        Remote face not detected
                    </span>
                </div>
            )}
        </div>
    );
};

export default Live2DViewer;